import * as Sentry from '@sentry/react'
import Conversation, {
  ConversationData,
  ConversationMessage,
  ConversationMessageKind,
  DateSeparator,
  LoadingMessages,
  NewMessageEventData,
  StateChanged,
  StateChangedData,
  UserJoined,
  UserJoinedEventData,
  UserMessages,
} from '../../api/conversation'
import { ConversationsAction } from '../../store/actions/conversations'
import {
  ADD_CONVERSATION,
  ADD_CONVERSATION_TAB,
  ADD_MESSAGE,
  ADD_MESSAGES,
  JOIN_CONVERSATION,
  REMOVE_CONVERSATION_TAB,
  REPLACE_LOADING_MESSAGE,
} from '../../store/constants'
import { ConversationTabsMap } from '../../store/types'
import {
  isLoadingMessagesData,
  isNewMessageEventData,
  isStateChangedEventData,
  isUserJoinedEventData,
} from '../../store/utils'
import { isSameDay } from '../../helpers'


export interface State {
  conversations: Map<number, ConversationData>
  sockets: Map<number, Conversation>
  messages: Map<number, ConversationMessage[]>
  // Tabs with small chat windows displayed at the bottom of screen
  tabs: ConversationTabsMap
}

export const initialState: State = {
  conversations: new Map(),
  sockets: new Map(),
  messages: new Map(),
  tabs: new Map(),
}

let nextId = 0
let nextLoadingMsgId = 0

// eslint-disable-next-line default-param-last
export function reducer(state: State = initialState, action: ConversationsAction): State {
  switch (action.type) {
  case ADD_CONVERSATION: {
    const { id } = action.data
    return {
      ...state,
      conversations: new Map(state.conversations).set(
        id,
        { ...action.data, knownMessages: new Set() },
      ),
      sockets: new Map(state.sockets).set(id, action.socket),
      messages: new Map(state.messages).set(
        id,
        [createMessage('message:loading', { refId: 0, direction: 'before' })],
      ),
    }
  }

  case JOIN_CONVERSATION: {
    const { conversationId, userId } = action.data
    const conversation = state.conversations.get(conversationId)
    if (!conversation) return state

    return {
      ...state,
      conversations: new Map(state.conversations).set(conversationId, {
        ...conversation,
        members: Array.from(new Set([...conversation.members, userId])),
        write: true,
      }),
    }
  }

  case ADD_MESSAGE: {
    const { id } = action.data
    let messages = state.messages.get(action.conversationId) || []
    const convData = state.conversations.get(action.conversationId)!

    if (!convData.knownMessages) {
      Sentry.captureMessage(
        `convData for conversation id: ${id} does not have required knownMessages property.`,
        'warning',
      )
      convData.knownMessages = new Set()
    }

    if (convData.knownMessages.has(id)) {
      return state
    }
    convData.knownMessages.add(id)

    let newMessage: ConversationMessage | undefined
    if (isNewMessageEventData(action.data)) {
      newMessage = createMessage('message:user', action.data)
    } else if (isUserJoinedEventData(action.data)) {
      newMessage = createMessage('message:userjoined', action.data)
    } else if (isStateChangedEventData(action.data)) {
      newMessage = createMessage('message:statechanged', action.data)
    }
    if (!newMessage) {
      console.error(`Unknown data was received in ADD_MESSAGE reducer`, action.data)
      return state
    }
    messages = mergeMessages([messages, [newMessage]])

    return {
      ...state,
      conversations: new Map(state.conversations).set(action.conversationId, { ...convData }),
      messages: new Map(state.messages).set(action.conversationId, messages),
    }
  }

  case ADD_MESSAGES: {
    const {
      data: {
        messages,
        conversationId,
        position,
        addLoadingMsgBefore,
        addLoadingMsgAfter,
      },
    } = action
    const convData = state.conversations.get(conversationId)!
    const currentMessages = state.messages.get(conversationId) || []

    const filteredMessages = messages.filter(msg => {
      if (convData.knownMessages.has(msg.id)) return false
      convData.knownMessages.add(msg.id)
      return true
    })

    const convertedMessages = convertMsgEventsToConvMessages(filteredMessages)

    let messagesToAdd: ConversationMessage[]

    const loadingMsgBefore = createMessage('message:loading', {
      refId: filteredMessages[filteredMessages.length-1].id,
      direction: 'before',
    })
    const loadingMsgAfter = createMessage('message:loading', {
      refId: filteredMessages[0].id,
      direction: 'after',
    })
    if (position === 'start') {
      messagesToAdd = mergeMessages([
        addLoadingMsgBefore ? [loadingMsgBefore] : undefined,
        convertedMessages,
        addLoadingMsgAfter ? [loadingMsgAfter] : undefined,
        currentMessages,
      ])
    } else if (position === 'end') {
      messagesToAdd = mergeMessages([
        currentMessages,
        addLoadingMsgBefore ? [loadingMsgBefore] : undefined,
        convertedMessages,
        addLoadingMsgAfter ? [loadingMsgAfter] : undefined,
      ])
    } else {
      console.error(`Unhandled position: ${position} for ADD_MESSAGES.`)
      return state
    }

    return {
      ...state,
      conversations: new Map(state.conversations).set(conversationId, { ...convData }),
      messages: new Map(state.messages).set(conversationId, messagesToAdd),
    }
  }

  case REPLACE_LOADING_MESSAGE: {
    const { conversationId, data: { before, after, ref, loadingMsgId, isLoadingDone } } = action
    let messages = state.messages.get(conversationId) || []
    const convData = state.conversations.get(conversationId)!

    let beforeLoadingMsgs: ConversationMessage[] = []
    let afterLoadingMsgs: ConversationMessage[] = []

    const indexOfLoadingMsg = messages.findIndex(
      msg => msg.kind === 'message:loading' && msg.id === loadingMsgId)
    let loadingMessageDirection = 'before'
    if (indexOfLoadingMsg > -1) {
      loadingMessageDirection = (messages[indexOfLoadingMsg] as LoadingMessages).direction

      beforeLoadingMsgs = messages.splice(0, indexOfLoadingMsg === 0 ? 0 : indexOfLoadingMsg)
      afterLoadingMsgs = messages.splice(1, messages.length)
    }

    // If message is duplicate do not add it to the conversations messages and store their ids here.
    const duplicates = new Set<number>()
    const newMessages = [...before, ref, ...after].reverse().filter(msg => {
      if (!msg) return false
      if (convData.knownMessages.has(msg.id)) {
        duplicates.add(msg.id)
        return false
      }
      convData.knownMessages.add(msg.id)
      return true
    }) as (NewMessageEventData | UserJoinedEventData | StateChangedData)[]

    if (newMessages.length === 0) {
      // There are no new messages to add. Just delete loading message.
      messages = mergeMessages([beforeLoadingMsgs, afterLoadingMsgs])
      return {
        ...state,
        messages: new Map(state.messages).set(action.conversationId, messages),
      }
    }

    const newConvertedMessages = convertMsgEventsToConvMessages(newMessages)

    if (isLoadingDone) {
      messages = mergeMessages([
        beforeLoadingMsgs,
        newConvertedMessages,
        afterLoadingMsgs,
      ])
    } else {
      // If loading is not done then add new LoadingMessage before / after
      // or on both ends of newConvertedMessages.
      switch (loadingMessageDirection) {
      case 'before': {
        const loadingMsg = createMessage('message:loading', {
          refId: newMessages[newMessages.length-1].id,
          direction: 'before',
        })
        messages = mergeMessages([
          beforeLoadingMsgs,
          [loadingMsg],
          newConvertedMessages,
          afterLoadingMsgs,
        ])
        break
      }
      case 'after': {
        const loadingMsg = createMessage('message:loading', {
          refId: newMessages[0].id,
          direction: 'after',
        })
        messages = mergeMessages([
          beforeLoadingMsgs,
          newConvertedMessages,
          [loadingMsg],
          afterLoadingMsgs,
        ])
        break
      }
      default: {
        throw new Error(`Unknown direction for LoadingMessages: ${loadingMessageDirection}`)
      }
      }
    }

    return {
      ...state,
      conversations: new Map(state.conversations).set(action.conversationId, { ...convData }),
      messages: new Map(state.messages).set(action.conversationId, messages),
    }
  }

  case ADD_CONVERSATION_TAB:
    return {
      ...state,
      tabs: new Map(state.tabs).set(action.data.convId, action.data),
    }

  case REMOVE_CONVERSATION_TAB: {
    const newTabs = new Map(state.tabs)
    newTabs.delete(action.data)

    return {
      ...state,
      tabs: newTabs,
    }
  }

  default:
    return state
  }
}

export type LoadingMessagesData = {
  refId: number,
  direction: 'before' | 'after',
}

/**
 * Create new ConversationMessage
 *
 * @param {ConversationMessageKind} kind
 * @param data Date for kind === 'message:separator'
 *             NewMessageEvent for kind === 'message:user'
 *             LoadingMessagesData for kind === 'message:loading',
 *             UserJoinedEventData for kind === 'message:userjoined'
 *             StateChangedData for kind === 'message:statechanged'
 */
const createMessage = (
  kind: ConversationMessageKind,
  data: Date | NewMessageEventData | LoadingMessagesData | UserJoinedEventData | StateChangedData,
): ConversationMessage => {
  switch (kind) {
  case 'message:separator':
    return {
      kind,
      date: data as Date,
    }

  case 'message:userjoined':
    if (isUserJoinedEventData(data)) {
      return {
        kind,
        id: data.id,
        date: data.date,
        users: data.users,
      }
    }

    throw new Error(`data is not of type UserJoinedEventData: ${data}`)

  case 'message:user':
    if (isNewMessageEventData(data)) {
      return {
        kind,
        id: nextId++,
        userId: data.userId,
        messages: [{
          id: data.id,
          time: data.time,
          message: data.message,
        }],
      }
    }

    throw new Error(`data is not of type NewMessageEventData: ${data}`)

  case 'message:loading':
    if (isLoadingMessagesData(data)) {
      return {
        kind,
        id: 'loading-' + nextLoadingMsgId++,
        refId: data.refId,
        direction: data.direction,
      }
    }

    throw new Error(`data is not of type LoadingMessagesData: ${data}`)

  case 'message:statechanged':
    if (isStateChangedEventData(data)) {
      return {
        kind,
        id: data.id,
        user: data.user,
        date: data.date,
        flags: data.flags,
      }
    }

    throw new Error(`data is not of type StateChangedEventData: ${data}`)

  default:
    throw new Error(`Wrong message kind: ${kind}.`)
  }
}

/**
 * Convert array of NewMessageEventData and UserJoinedEvetData objects
 * into ConversationMessage array.
 */
const convertMsgEventsToConvMessages = (
  messages: (NewMessageEventData | UserJoinedEventData | StateChangedData)[],
): ConversationMessage[] => {
  const convertedMessages: ConversationMessage[] = []

  // We will proess every message and unshift it to the convertedMessages as proper object.
  // First element in array will be always DateSeparator. Messages will be inserted below
  // this separator if they are from the same day and above if not (new separator should be created)
  for (const msg of messages) {
    // First message should be always DateSeparator or undefined when there are no messages.
    const first = convertedMessages[0] as (DateSeparator | undefined)

    if (!first) {
      // If there were no messages just add new one.
      const date = isNewMessageEventData(msg) ? msg.time : msg.date

      convertedMessages.push(createMessage('message:separator', date))

      if (isStateChangedEventData(msg)) {
        convertedMessages.push(createMessage('message:statechanged', msg))
        continue
      }

      if (isUserJoinedEventData(msg)) {
        convertedMessages.push(createMessage('message:userjoined', msg))
        continue
      }

      if (isNewMessageEventData(msg)) {
        convertedMessages.push(createMessage('message:user', msg))
        continue
      }

      throw new Error(`msg have undefined type: ${msg}`)
    }

    const thisMsgTime = isNewMessageEventData(msg) ? msg.time : msg.date

    // Second message should always be UserMessages, UserJoined or StateChanged.
    const second = convertedMessages[1] as UserMessages | UserJoined | StateChanged
    const sameDay = second.kind === 'message:user'
      ? isSameDay(second.messages[second.messages.length - 1].time, thisMsgTime)
      : isSameDay(second.date, thisMsgTime)

    // If msg is UserJoinedEventData determine if it is from the same day as previouse message
    // and add it to the array or create new dateseparator.
    if (isUserJoinedEventData(msg)) {
      if (sameDay) {
        // If msg is from the same day, then take DateSeparator out,
        // add new UserJoined and put DateSeparator in.
        const separator = convertedMessages.shift()!
        convertedMessages.unshift(createMessage('message:userjoined', msg))
        convertedMessages.unshift(separator)
        continue
      }

      // If msg is from the other day, then just add it as new UserJoined
      // and create new DateSeparator.
      convertedMessages.unshift(createMessage('message:userjoined', msg))
      convertedMessages.unshift(createMessage('message:separator', thisMsgTime))

      continue
    }

    // If msg is StateChangedData determine if it is from the same day as previouse message
    // and add it to the array or create new dateseparator.
    if (isStateChangedEventData(msg)) {
      if (sameDay) {
        // If msg is from the same day, then take DateSeparator out,
        // add new StateChanged and put DateSeparator in.
        const separator = convertedMessages.shift()!
        convertedMessages.unshift(createMessage('message:statechanged', msg))
        convertedMessages.unshift(separator)
        continue
      }

      // If msg is from the other day, then just add it as new StateChanged
      // and create new DateSeparator.
      convertedMessages.unshift(createMessage('message:statechanged', msg))
      convertedMessages.unshift(createMessage('message:separator', thisMsgTime))

      continue
    }

    // Current message is NewMessageEventData
    const { id, userId, time, message } = msg as NewMessageEventData

    // If previous message was UserJoined or StateChanged
    // then we want to add current message as new UserMessages
    if (second.kind === 'message:userjoined' || second.kind === 'message:statechanged') {
      if (sameDay) {
        // If msg is from the same day, then take DateSeparator out,
        // add new UserMessages and put DateSeparator in.
        const separator = convertedMessages.shift()!
        convertedMessages.unshift(createMessage('message:user', msg))
        convertedMessages.unshift(separator)
        continue
      }

      // If msg is from the other day, then just add it as new UserMessages
      // and create new DateSeparator.
      convertedMessages.unshift(createMessage('message:user', msg))
      convertedMessages.unshift(createMessage('message:separator', time))

      continue
    }

    // Second message is UserMessages and current msg is NewMessageEvetData
    // We will determine if it's from the same user and from same day
    // If it is we can add this message to UserMessages.messages array

    // Check if new msg is from the same user.
    if (second.userId === userId) {
      if (sameDay) {
        // If msg is from the same day as second message, just unshift it.
        second.messages.unshift({ id, time, message })
      } else {
        // If it is from other day then add it as new UserMessages
        // and add new DateSeparator.
        convertedMessages.unshift(createMessage('message:user', msg))
        convertedMessages.unshift(createMessage('message:separator', time))
      }

      continue
    }

    // Current msg is from other user. Lets just determine if it's from same day or not.

    if (sameDay) {
      // If msg is from the same day, then take DateSeparator out,
      // add new UserMessages and put DateSeparator in.
      const separator = convertedMessages.shift()!
      convertedMessages.unshift(createMessage('message:user', msg))
      convertedMessages.unshift(separator)

      continue
    }

    // If msg is from the other day, then just add is as new UserMessages
    // and create new DateSeparator.
    convertedMessages.unshift(createMessage('message:user', msg))
    convertedMessages.unshift(createMessage('message:separator', time))
  }

  return convertedMessages
}

/**
 * Merge arrays of ConversationMessages into one.
 * This function assumess that there are no duplicates and messages are in correct order.
 */
const mergeMessages = (
  messages: ((ConversationMessage | undefined)[] | undefined)[],
): ConversationMessage[] => {
  const merged: ConversationMessage[] = []

  let currDay: Date | null = null
  for (const msgs of messages) {
    if (!msgs) continue
    for (const msg of msgs) {
      if (!msg) continue
      // Keep LoadingMessages, UserJoined and StateChange
      if (
        msg.kind === 'message:loading'
        || msg.kind === 'message:userjoined'
        || msg.kind === 'message:statechanged'
      ) {
        merged.push(msg)
        continue
      }

      // Do not duplicate DateSeparators
      if (msg.kind === 'message:separator') {
        if (!currDay || !isSameDay(currDay, msg.date)) {
          currDay = msg.date
          merged.push(msg)
        }
        continue
      }

      // Try to merge UserMessages to previous UserMessages
      if (msg.kind === 'message:user') {
        const last = merged.pop()
        if (last && last.kind === 'message:user') {
          if (
            last.userId === msg.userId
            && (!currDay || isSameDay(currDay, msg.messages[0].time))
          ) {
            last.messages = [
              ...last.messages,
              ...msg.messages,
            ]
            merged.push(last)
            continue
          }
        }
        if (last) {
          merged.push(last)
        }
        merged.push(msg)
        continue
      }
    }
  }

  return merged
}
